import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { MeetingSummarySourceCard } from '@/components/meeting-summaries/MeetingSummarySourceCard';
import { useOrg } from '@/contexts/OrgContext';
import type { SourceConnectionStatus } from '@/hooks/integrations/meeting-summaries-sources-status';
import { useMeetingSummariesSourcesStatus } from '@/hooks/integrations/meeting-summaries-sources-status';
import { useProfile } from '@/hooks/profile/useProfile';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Toggle } from '@/lib/base/forms/toggle';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { QueryErrorState } from '@/lib/components';
import { MEETING_SUMMARY_SOURCES } from '@/lib/integrations/meeting-summaries/source-config';
import { useViewHealthMarker } from '@/lib/view-health';

import { useUser } from '../../../../allauth_auth';
import { OAuthBanners } from './OAuthBanners';

const STATUS_ANNOUNCEMENTS: Record<SourceConnectionStatus, string> = {
  connected: 'connected',
  connected_no_delivery: 'connected, but summaries are not arriving',
  disconnected: 'disconnected',
  auth_failed: 'needs reconnecting',
};

export function MeetingSummariesSection() {
  const [searchParams] = useSearchParams();
  const { currentOrg } = useOrg();
  const user = useUser();
  const { hasPermission } = usePermissions();
  const isNotetakerEnabled = useFeatureFlag('recall_notetaker');
  useViewHealthMarker('settings-section-ready');
  const {
    formData,
    membershipId,
    membershipLoading,
    membershipLoadError,
    captureNotesWithoutCalendarMeetingSaving,
    captureNotesWithoutCalendarMeetingError,
    setCaptureNotesWithoutCalendarMeeting,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });

  const orgId = currentOrg?.id;
  const canConnect = hasPermission(
    'ahoy_external_integration',
    'ahoy_object_created'
  );
  const canModify = hasPermission(
    'ahoy_external_integration',
    'ahoy_property_updated'
  );

  const {
    data: statusData,
    isLoading,
    isError,
    error,
    refetch,
  } = useMeetingSummariesSourcesStatus(orgId);

  const [fathomSuccess, setFathomSuccess] = useState(false);
  const [fathomError, setFathomError] = useState<string | null>(null);
  const [granolaSuccess, setGranolaSuccess] = useState(false);
  const [granolaError, setGranolaError] = useState<string | null>(null);
  const [firefliesError, setFirefliesError] = useState<string | null>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const prevStatusRef = useRef<typeof statusData>(undefined);

  useEffect(() => {
    const fathomSuccessParam = searchParams.get('fathom_success');
    const fathomErrorParam = searchParams.get('fathom_error');
    setFathomSuccess(fathomSuccessParam === 'true');
    setFathomError(
      fathomSuccessParam === 'true' ? null : (fathomErrorParam ?? null)
    );
    const granolaSuccessParam = searchParams.get('granola_success');
    const granolaErrorParam = searchParams.get('granola_error');
    setGranolaSuccess(granolaSuccessParam === 'true');
    setGranolaError(
      granolaSuccessParam === 'true' ? null : (granolaErrorParam ?? null)
    );
    const firefliesErrorParam = searchParams.get('fireflies_error');
    setFirefliesError(firefliesErrorParam ?? null);
  }, [searchParams]);

  useEffect(() => {
    if (!statusData || !prevStatusRef.current) {
      prevStatusRef.current = statusData;
      return;
    }
    const prev = prevStatusRef.current;
    for (const source of MEETING_SUMMARY_SOURCES) {
      const prevStatus = prev[source.id]?.connection_status;
      const nextStatus = statusData[source.id]?.connection_status;
      if (prevStatus !== nextStatus && nextStatus) {
        setLiveAnnouncement(
          `${source.name} is now ${STATUS_ANNOUNCEMENTS[nextStatus] ?? nextStatus}`
        );
      }
    }
    prevStatusRef.current = statusData;
  }, [statusData]);

  const dismissFathomSuccess = useCallback(() => setFathomSuccess(false), []);
  const dismissFathomError = useCallback(() => setFathomError(null), []);
  const dismissGranolaSuccess = useCallback(() => setGranolaSuccess(false), []);
  const dismissGranolaError = useCallback(() => setGranolaError(null), []);
  const dismissFirefliesError = useCallback(() => setFirefliesError(null), []);
  const handleRetry = useCallback(() => void refetch(), [refetch]);

  const handleCaptureNotesToggle = useCallback(
    (checked: boolean) => {
      setCaptureNotesWithoutCalendarMeeting(checked).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setCaptureNotesWithoutCalendarMeeting]
  );

  const allDisconnected =
    !isError &&
    !!statusData &&
    MEETING_SUMMARY_SOURCES.every(
      s => statusData[s.id]?.connection_status === 'disconnected'
    );

  return (
    <div className='space-y-8'>
      <div role='status' aria-live='polite' className='sr-only'>
        {[
          liveAnnouncement,
          captureNotesWithoutCalendarMeetingError,
          membershipLoadError,
        ]
          .filter(Boolean)
          .join('. ')}
      </div>

      <div>
        <h1 className='text-lg font-semibold text-fg-primary'>
          Meeting summaries
        </h1>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Ahoy captures summaries from the meeting tools you already use.
          Connect a source below, or check your setup to make sure summaries
          come through.
        </p>
      </div>

      <div className='space-y-4'>
        {allDisconnected && (
          <div className='card-refined p-4'>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              Get started by connecting a meeting source below. Each source
              captures summaries differently – pick the one that matches how
              your team meets.
            </p>
          </div>
        )}

        {isNotetakerEnabled && orgId && (
          <div className='card-refined p-4'>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              Looking for the Ahoy Notetaker? Configure how Ahoy joins your
              meetings in{' '}
              <Link
                to={`/${orgId}/settings/notetaker`}
                className='font-medium text-fg-brand-primary hover:underline'
              >
                Notetaker settings
              </Link>
              .
            </p>
          </div>
        )}

        <OAuthBanners
          fathomSuccess={fathomSuccess}
          fathomError={fathomError}
          granolaSuccess={granolaSuccess}
          granolaError={granolaError}
          firefliesError={firefliesError}
          onDismissFathomSuccess={dismissFathomSuccess}
          onDismissFathomError={dismissFathomError}
          onDismissGranolaSuccess={dismissGranolaSuccess}
          onDismissGranolaError={dismissGranolaError}
          onDismissFirefliesError={dismissFirefliesError}
        />

        {isLoading && (
          <div aria-busy='true' className='space-y-3'>
            {MEETING_SUMMARY_SOURCES.map(s => (
              <Skeleton key={s.id} variant='rectangular' height={96} />
            ))}
          </div>
        )}

        {isError && !isLoading && (
          <QueryErrorState
            error={error}
            label="We couldn't load the source statuses"
            onRetry={handleRetry}
            size='sm'
          />
        )}

        {!isLoading && !isError && statusData && orgId && (
          <div className='space-y-3'>
            {MEETING_SUMMARY_SOURCES.map(source => (
              <MeetingSummarySourceCard
                key={source.id}
                orgId={orgId}
                config={source}
                status={statusData[source.id]}
                canConnect={canConnect}
                canModify={canModify}
              />
            ))}
          </div>
        )}

        {membershipLoadError && (
          <div>
            <AlertFloating
              color='error'
              title={membershipLoadError}
              description="This setting can't be changed until the issue is resolved."
            />
          </div>
        )}
        {!membershipLoadError && captureNotesWithoutCalendarMeetingError && (
          <div>
            <AlertFloating
              color='error'
              title="Couldn't save your preference"
              description={captureNotesWithoutCalendarMeetingError}
            />
          </div>
        )}
        <ButtonList aria-label='Capture notes settings'>
          <ButtonList.Item
            trailing={
              <Toggle
                isSelected={formData.capture_notes_without_calendar_meeting}
                onChange={handleCaptureNotesToggle}
                isDisabled={
                  captureNotesWithoutCalendarMeetingSaving ||
                  membershipLoading ||
                  !!membershipLoadError ||
                  membershipId === null
                }
                aria-label='Capture notes without a calendar meeting'
              />
            }
          >
            <p className='text-sm font-medium text-fg-primary'>
              Capture notes without a calendar meeting
            </p>
            <p className='text-sm text-fg-tertiary'>
              When on, we&apos;ll save notes from calls that aren&apos;t on your
              calendar and create a meeting for them. Off by default.
            </p>
          </ButtonList.Item>
        </ButtonList>
      </div>
    </div>
  );
}
